import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Wheel } from "react-custom-roulette";
import "./styles/tokens.css";
import "./App.css";
import confetti from "canvas-confetti";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLogin from "./components/AdminLogin/AdminLogin";
import AIChat from "./components/AIChat/AIChat";
import {
  validateUser,
  fetchUserData,
  fetchSegments,
  saveCssChanges,
  fetchCarkCss,
  resetToDefaultCss,
  fetchUserHistory,
  spinWheel,
  sendSpinCountRequest,
} from "./services/userService";
import { isAdminRoute } from "./helper/apiHelper";
import Loading from "./components/Loading/Loading";
import Header from "./components/Header/Header";
import WheelEditor from "./components/WheelEditor/WheelEditor";
import DesignEditor from "./components/DesignEditor/DesignEditor";
import AdminButtons from "./components/AdminButtons/AdminButtons";
import CustomAlert from "./components/CustomAlert/CustomAlert";
import ThemeSelector from "./components/ThemeSelector/ThemeSelector";
import NoSpinAlert from "./components/NoSpinAlert/NoSpinAlert";
import StatusAlert from "./components/StatusAlert/StatusAlert";
import ResetConfirmationModal from "./components/ResetConfirmationModal/ResetConfirmationModal";

// RGB değerlerini hesaplayan yardımcı fonksiyon
const getRGB = (hex) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length !== 7) {
    return "9, 249, 132";
  }
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return "9, 249, 132";
    return `${r}, ${g}, ${b}`;
  } catch (error) {
    console.error("Renk dönüşüm hatası:", error);
    return "9, 249, 132";
  }
};

// Font ismini döndüren yardımcı fonksiyon (CSS custom property değerleri için tırnaksız)
const quoteFont = (font) => {
  if (!font) return 'sans-serif';
  return font.replace(/['"]/g, "");
};

// Hex rengi belirli oranda koyulaştır (perde için tema renginden koyu varyant türetir)
// amount: 0 = aynı, 1 = siyah. Tipik 0.5-0.75 arası.
const darkenHex = (hex, amount = 0.5) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length !== 7) return "#4d0a0a";
  try {
    const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amount)));
    const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amount)));
    const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amount)));
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch {
    return "#4d0a0a";
  }
};

// Hex renk kodundan hue (ton) derecesi çıkar
const hexToHue = (hex) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length < 7) return 0;
  try {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let hue = 0;
    if (max !== min) {
      const d = max - min;
      if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) * 60;
      else if (max === g) hue = ((b - r) / d + 2) * 60;
      else hue = ((r - g) / d + 4) * 60;
    }
    return hue;
  } catch { return 0; }
};

// Kazanan segment renginden parlak konfeti paleti türet
// (analog + komplementer + altın + beyaz vurgu)
const hslToHex = (h, s, l) => {
  const sat = Math.min(1, Math.max(0, s));
  const lit = Math.min(0.85, Math.max(0.4, l));
  const a = sat * Math.min(lit, 1 - lit);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = lit - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const deriveConfettiPalette = (hex) => {
  const fallback = ["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96E6A1", "#FFFFFF"];
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length < 7) return fallback;
  const h = hexToHue(hex);
  return [
    hslToHex(h, 0.85, 0.6),               // baz, parlatılmış
    hslToHex((h + 45) % 360, 0.8, 0.62),  // analog +45°
    hslToHex((h - 45 + 360) % 360, 0.8, 0.62), // analog -45°
    hslToHex((h + 180) % 360, 0.75, 0.58), // komplementer
    "#FFD700",                             // altın sabit
    "#FFFFFF",                             // beyaz vurgu
  ];
};

function App() {
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [showWheelEditor, setShowWheelEditor] = useState(false);
  const [showDesignEditor, setShowDesignEditor] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [showAIChat, setShowAIChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("Kullanıcı");
  const [spinCount, setSpinCount] = useState(0);
  const [status, setStatus] = useState(false);
  const [segments, setSegments] = useState([]);
  const [invalidAccess, setInvalidAccess] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  // Sadece UI animasyonu için — kazanım anı 700ms vurgu pulse'u
  const [isWinning, setIsWinning] = useState(false);
  const [logoSrc, setLogoSrc] = useState("/default-logo.png");
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [fontLoadedTrigger, setFontLoadedTrigger] = useState(0);
  const [showNoSpinAlert, setShowNoSpinAlert] = useState(false);
  const [statusModal, setStatusModal] = useState({ show: false, type: 'success', title: '', message: '' });
  const [isRequestingSpinCount, setIsRequestingSpinCount] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [activeBackground, setActiveBackground] = useState(null);

  // CSS değişikliklerini takip etmek için yeni bir state
  // Lazy init: localStorage'tan geçen seferki tema + custom logo yüklenir —
  // böylece Loading ekranı default mavi değil, son tema renkleriyle açılır.
  const [cssState, setCssState] = useState(() => {
    const defaults = {
      backgroundColor1: "#0b1528",
      backgroundColor2: "#0f2347",
      textColor1: "#00f0ff",
      textColor2: "#d4af37",
      borderColor: "#00f0ff",
      pointerColor: "#00f0ff",
      wheelGlowColor: "#00f0ff",
      wheelGlowOpacity: 0.35,
      radiusLineColor: "rgba(0, 240, 255, 0.4)",
      buttonGradient1: "#0b1528",
      buttonGradient2: "#0f2347",
      buttonTextColor: "#00f0ff",
      buttonBorderColor: "#00f0ff",
      buttonGlowColor: "#00f0ff",
      buttonDisabled1: "#101f38",
      buttonDisabled2: "#0a1324",
      buttonDisabledText: "#1e3b6e",
      buttonDisabledBorder: "#101f38",
      headerBg: "rgba(11, 21, 40, 0.7)",
      headerBorderColor: "rgba(0, 240, 255, 0.15)",
      backgroundGradient1: "#070c16",
      backgroundGradient2: "#0c1527",
      headerItemBg: "rgba(11, 21, 40, 0.6)",
      headerItemText: "#ffffff",
      headerItemBorder: "rgba(0, 240, 255, 0.15)",
      headerItemHoverBg: "rgba(11, 21, 40, 0.8)",
      headerItemHoverBorder: "rgba(0, 240, 255, 0.45)",
      headerEmojiColor: "#00f0ff",
      headerIconColor: "#00f0ff",
      headerTextColor: "#ffffff",
      headerButtonBg: "rgba(11, 21, 40, 0.7)",
      headerButtonHoverBg: "rgba(11, 21, 40, 0.8)",
      // Yeni modal renkleri
      modalBg: "#0b1528",
      modalBorderColor: "#00f0ff",
      modalHeaderColor: "#FFFFFF",
      modalTextColor: "#e2e8f0",
      modalButtonGradient1: "#0b1528",
      modalButtonGradient2: "#0f2347",
      modalButtonText: "#FFFFFF",
      // Geçmiş modal renkleri
      historyModalBg: "#0b1528",
      historyModalBorder: "#00f0ff",
      historyHeaderColor: "#FFFFFF",
      historyCloseColor: "#FFFFFF",
      historyDateColor: "#64748b",
      historyBadgeBg: "#00f0ff",
      historyBadgeText: "#0b1528",
      historyInfoLabel: "#64748b",
      historyInfoValue: "#cbd5e1",
      historyNoteColor: "#64748b",
      historyItemBg: "#0f2347",
      historyItemBorder: "#00f0ff",
      historyHeaderBg: "#0b1528",
      // No Spin Alert renkleri
      noSpinOverlayBg: "rgba(7, 12, 22, 0.95)",
      noSpinModalGradient1: "#0b1528",
      noSpinModalGradient2: "#0f2347",
      noSpinModalBorder: "rgba(0, 240, 255, 0.15)",
      noSpinModalTitle: "#FFFFFF",
      noSpinModalText: "rgba(255, 255, 255, 0.8)",
      // Dekorasyon renkleri
      ringAccentColor: "#00f0ff",
      ambientGlowColor: "#00f0ff",
      standAccentColor: "#0a1324",
      cloverColor: "#00f0ff",
      // Arkaplan görseli
      customBgEnabled: false,
      customBgImage: null,
      customLogoEnabled: false,
      customLogoImage: null,
      logoMarginTop: 0,
      logoMarginBottom: 0,
      logoMarginLeft: 0,
      logoMarginRight: 0,
      logoPaddingTop: 0,
      logoPaddingBottom: 0,
      logoPaddingLeft: 0,
      logoPaddingRight: 0,
      logoWidth: 0,
      logoHeight: 0,
      customAvatarEnabled: false,
      customAvatarImage: null,
      // Header — granular tema alanları (Faz A)
      welcomeBg: null,
      welcomeBorder: null,
      welcomeBorderHover: null,
      avatarBorder: null,
      avatarGlow: null,
      avatarBgColor: null,
      greetingColor: null,
      usernameColor: null,
      subtitleColor: null,
      chipBg: null,
      chipBorder: null,
      chipText: null,
      actionBtnBg: null,
      actionBtnBorder: null,
      actionBtnText: "#FFFFFF",
      actionBtnHoverBg: null,
      actionBtnHoverBorder: null,
      actionBtnHoverText: "#FFFFFF",
      // Modal granular tema alanları (Faz B)
      modalOverlayBg: null,
      historyOverlayBg: null,
      noSpinBg: null,
      noSpinBorder: null,
      noSpinTitle: null,
      noSpinText: null,
      noSpinX: null,
      noSpinClaimBg: null,
      noSpinClaimText: null,
      noSpinCancelBg: null,
      noSpinCancelText: null,
      resetOverlayBg: null,
      resetBg: null,
      resetBorder: null,
      resetTitle: null,
      resetText: null,
      resetCancelBg: null,
      resetCancelText: null,
      resetConfirmBg: null,
      resetConfirmText: null,
      // Yazı tipleri
      spinButtonFont: "Poppins",
      wheelSlicesFont: "Montserrat",
      wheelSlicesFontSize: 20,
      // Long footer logo görünürlüğü
      longLogoVisible: true,
      // Yeni Tasarım Seçenekleri
      wheelType: "cosmic",
      wheelFrameStyle: "cosmic",
      wheelFrameColor1: "#00f0ff",
      wheelFrameColor2: "#7000ff",
      centerButtonStyle: "cosmic",
      centerButtonCrystalColor: "#00f0ff",
      centerButtonTextColor: "#00f0ff",
      pointerStyle: "cosmic",
      wheelOverlayShine: true,
      segmentIcons: {},
    };
    try {
      const cached = localStorage.getItem('cssStateCache');
      if (cached) return { ...defaults, ...JSON.parse(cached) };
    } catch {}
    return defaults;
  });

  // cssState değiştikçe localStorage'a yaz (büyük image'lar dahil küçükse) — sonraki refresh'te instant tema
  useEffect(() => {
    try {
      const { customBgImage, customLogoImage, customAvatarImage, ...rest } = cssState;
      const cache = { ...rest };
      if (customLogoImage && typeof customLogoImage === 'string' && customLogoImage.length < 250000) {
        cache.customLogoImage = customLogoImage;
      }
      if (customBgImage && typeof customBgImage === 'string' && customBgImage.length < 250000) {
        cache.customBgImage = customBgImage;
      }
      if (customAvatarImage && typeof customAvatarImage === 'string' && customAvatarImage.length < 100000) {
        cache.customAvatarImage = customAvatarImage;
      }
      localStorage.setItem('cssStateCache', JSON.stringify(cache));
    } catch {}
  }, [cssState]);

  const [isLoading, setIsLoading] = useState(true);

  // State ekleyelim
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // useRef ekleyelim
  const winSoundRef = useRef(new Audio("/win.mp3"));

  // ─── Çark dönüş sesi — Web Audio API ile dinamik tick (mekanik çark hissi) ───
  // Başta hızlı (~50ms), çark yavaşladıkça aralık uzar (~300ms). Tek ses dosyasından
  // çok daha gerçekçi: gerçek bir piyango çarkı gibi tıkırdar.
  const audioCtxRef = useRef(null);
  const tickTimeoutRef = useRef(null);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    return audioCtxRef.current;
  };

  // Tek tick: short noise burst → bandpass filter → exponential decay
  // Sonuç: tahta peg'in tahta gövdeye çarpma sesi (gerçek piyango çarkı tıkırtısı)
  const playTick = () => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const t = ctx.currentTime;

    // 25ms white noise buffer + sample-bazlı decay (mekanik impact karakteri)
    const sampleRate = ctx.sampleRate;
    const bufferSize = Math.floor(0.025 * sampleRate);
    const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 1.6);
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    // Bandpass: mekanik "tık" tonu (~1500Hz body resonance)
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1500, t);
    filter.Q.setValueAtTime(3.5, t);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.45, t + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start(t);
    noise.stop(t + 0.05);
  };

  // Tick generator: çarkın gerçek dönüş profiline uyumlu (yavaş → hızlı → yavaş).
  //   Faz 1 (0 - accelMs):       hızlanma  — interval 350ms → 60ms
  //   Faz 2 (accelMs - estimatedMs): yavaşlama — interval 60ms → 350ms
  //   maxMs güvenlik üst sınırı.
  const startWheelTicks = (estimatedMs = 8500, maxMs = 20000, accelMs = 2500) => {
    stopWheelTicks();
    const start = performance.now();
    const loop = () => {
      const elapsed = performance.now() - start;
      if (elapsed >= maxMs) return;
      playTick();

      let interval;
      if (elapsed < accelMs) {
        // Hızlanma fazı (ease-out): tk........tk....tk..tk.tk.tk.tk
        const t = elapsed / accelMs;             // 0 → 1
        const eased = 1 - Math.pow(1 - t, 2);    // ease-out
        interval = 350 - eased * 290;            // 350 → 60
      } else {
        // Yavaşlama fazı (pow 2): tk.tk.tk.tk..tk....tk........tk
        const t = Math.min((elapsed - accelMs) / Math.max(estimatedMs - accelMs, 1), 1);
        interval = 60 + Math.pow(t, 2.0) * 290;  // 60 → 350
      }
      tickTimeoutRef.current = setTimeout(loop, interval);
    };
    loop();
  };

  const stopWheelTicks = () => {
    if (tickTimeoutRef.current) {
      clearTimeout(tickTimeoutRef.current);
      tickTimeoutRef.current = null;
    }
  };

  // CSS değişikliklerini kaydetme fonksiyonu
  const handleSaveCssChanges = async () => {
    try {
      setIsLoading(true); // Loading göster
      const result = await saveCssChanges(cssState);

      if (result.success) {
        // Wheel glow özelliklerini doğrudan uygula (kayıt sonrası)
        if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
          try {
            const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
              cssState.wheelGlowOpacity
            })`;
            document.querySelectorAll(".wheel-container").forEach((container) => {
              container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
            });
          } catch (error) {
            console.error("Kayıt sonrası wheel glow güncellenirken hata:", error);
          }
        }

        setAlertConfig({
          show: true,
          message: "CSS değişiklikleri başarıyla kaydedildi",
          type: "success",
        });
      } else {
        setAlertConfig({
          show: true,
          message: result.message || "CSS değişiklikleri kaydedilemedi",
          type: "error",
        });
      }
    } catch (error) {
      console.error("CSS kaydetme hatası:", error);
      setAlertConfig({
        show: true,
        message: "CSS değişiklikleri kaydedilirken bir hata oluştu",
        type: "error",
      });
    } finally {
      setIsLoading(false); // Loading'i kaldır
    }
  };

  useEffect(() => {
    const setBackgroundStyle = () => {
      const isMobile = window.innerWidth <= 768;

      setActiveBackground(
        isMobile ? "images/bg-image-mobile.webp" : "images/bg-image.webp"
      );
    };

    setBackgroundStyle();
    window.addEventListener("resize", setBackgroundStyle);

    return () => window.removeEventListener("resize", setBackgroundStyle);
  }, []);

  // Çark verilerini API'den al
  useEffect(() => {
    const loadSegments = async () => {
      const segmentData = await fetchSegments();
      if (segmentData) {
        setSegments(segmentData);
      }
    };

    loadSegments();
  }, []);

  const data = useMemo(() => {
    if (!segments.length) return [];

    // Uzun segment isimlerini KELİME SINIRINDAN satırlara böl
    // (eski versiyon kelime ortasından kırıyordu: "Starlight P\nrincess" gibi)
    const wrapByWord = (text, maxLen = 16) => {
      if (text.length <= maxLen) return text;
      const words = text.split(/\s+/);
      const lines = [];
      let current = '';
      for (const w of words) {
        const tentative = current ? `${current} ${w}` : w;
        if (tentative.length <= maxLen) {
          current = tentative;
        } else {
          if (current) lines.push(current);
          // Tek kelime maxLen'den uzunsa karakter bazlı kır
          if (w.length > maxLen) {
            const chunks = w.match(new RegExp(`.{1,${maxLen}}`, 'g')) || [w];
            lines.push(...chunks.slice(0, -1));
            current = chunks[chunks.length - 1];
          } else {
            current = w;
          }
        }
      }
      if (current) lines.push(current);
      return lines.join('\n');
    };

    return segments.map((segment, index) => {
      // Uzun isimleri 2-3 kısa satıra böl → her satır kısalır, font büyük kalabilir.
      // (Kütüphanenin canvas çizimi \n'i çok satır olarak çizecek şekilde yamalandı.)
      const formattedSegment = wrapByWord(segment, 13);
      
      // Manuel emoji/ikon ekleme
      const icon = cssState.segmentIcons?.[index] || cssState.segmentIcons?.[String(index)];
      const finalOption = icon ? `${icon}\n${formattedSegment}` : formattedSegment;

      const lines = finalOption.split('\n');
      const longest = Math.max(...lines.map((l) => l.length));
      // Font, EN UZUN SATIRA göre belirlenir (radyal sığması için); çok satırda biraz kısılır.
      // Canvas fontu çarka oranlı olduğundan çark küçülünce yazı da orantılı küçülür.
      let fontSize = cssState.wheelSlicesFontSize ? parseInt(cssState.wheelSlicesFontSize, 10) : (longest > 13 ? 17 : longest > 10 ? 20 : longest > 7 ? 24 : 28);
      if (!cssState.wheelSlicesFontSize) {
        if (lines.length >= 3) fontSize -= 4;
        else if (lines.length === 2) fontSize -= 1;
      }
      return {
        option: finalOption,
        style: {
          backgroundColor:
            index % 2 === 0 ? cssState.backgroundColor1 : cssState.backgroundColor2,
          textColor: index % 2 === 0 ? cssState.textColor1 : cssState.textColor2,
          fontSize,
          fontSizeInner: fontSize,
        },
      };
    });
  }, [
    segments,
    cssState.backgroundColor1,
    cssState.backgroundColor2,
    cssState.textColor1,
    cssState.textColor2,
    cssState.wheelSlicesFontSize,
    cssState.segmentIcons,
  ]);

  // handleSpinClick fonksiyonunu güncelleyelim
  const handleSpinClick = useCallback(async () => {
    if (!mustSpin && segments.length > 0) {
      // Spin hakkı kontrolü
      if (!spinCount || spinCount <= 0) {
        setShowNoSpinAlert(true);
        return;
      }

      try {
        setIsSpinning(true);
        const userId = getUserIdFromUrl();
        const result = await spinWheel(userId);

        if (result.success) {
          const prizeIndex = segments.findIndex((segment) => segment === result.prize);

          if (prizeIndex !== -1) {
            setPrizeNumber(prizeIndex);
            setMustSpin(true);
            setSpinCount(result.remainingSpins);

            // Çark tick generator — animasyon süresi boyunca çalar, handleStopSpinning ile durur
            setTimeout(() => {
              startWheelTicks();
            }, 250);
          } else {
            setAlertConfig({
              show: true,
              message: "Beklenmeyen bir hata oluştu",
              type: "error",
            });
          }
        } else {
          setAlertConfig({
            show: true,
            message: result.message || "Çark çevirme işlemi başarısız",
            type: "error",
          });
        }
      } catch (error) {
        setAlertConfig({
          show: true,
          message: "Çark çevirme sırasında bir hata oluştu",
          type: "error",
        });
      } finally {
        setIsSpinning(false);
      }
    }
  }, [mustSpin, segments, spinCount]);

  const handleStopSpinning = useCallback(() => {
    setMustSpin(false);
    setIsWinning(true);
    // Modal'ı 700ms gecikmeli aç — kazanım pulse animasyonu için
    setTimeout(() => {
      setIsWinning(false);
      setShowPrizeModal(true);
    }, 700);

    // Güvenli ses geçişi: önce çark tick'lerini durdur, sonra kazanma sesini çal
    stopWheelTicks();

    const winAudio = winSoundRef.current;
    winAudio.currentTime = 0;
    winAudio.play().catch(() => {});

    // 🎨 Konfeti paletini kazanan segment renginden türet
    const winColor = data[prizeNumber]?.style?.backgroundColor;
    const palette = deriveConfettiPalette(winColor);
    const sidePalette = [palette[0], palette[3], palette[4]]; // havai fişek için 3 vurgu
    const starPalette = [palette[4], palette[0], palette[1]]; // yıldız için altın+baz+analog

    // 🎆 Havai Fişek + Konfeti Efekti
    const duration = 3000;
    const end = Date.now() + duration;

    // İlk patlama
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { y: 0.6 },
      colors: palette,
      zIndex: 9999,
    });

    // Havai fişek efekti — sol ve sağdan
    const interval = setInterval(() => {
      if (Date.now() > end) {
        clearInterval(interval);
        return;
      }
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: sidePalette,
        zIndex: 9999,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: sidePalette,
        zIndex: 9999,
      });
    }, 150);

    // Yıldız konfetisi — biraz gecikmeli
    setTimeout(() => {
      confetti({
        particleCount: 60,
        spread: 100,
        origin: { y: 0.35 },
        shapes: ['star'],
        colors: starPalette,
        scalar: 1.2,
        zIndex: 9999,
      });
    }, 800);
  }, [data, prizeNumber]);

  const wheelProps = useMemo(
    () => ({
      mustStartSpinning: mustSpin,
      prizeNumber: prizeNumber,
      data: data,
      onStopSpinning: handleStopSpinning,
      outerBorderColor: cssState.borderColor,
      outerBorderWidth: 8,
      innerRadius: 10,
      innerBorderColor: cssState.pointerColor,
      innerBorderWidth: 2,
      radiusLineColor: cssState.radiusLineColor,
      radiusLineWidth: 1,
      fontSize: cssState.wheelSlicesFontSize ? parseInt(cssState.wheelSlicesFontSize, 10) : 20,
      textDistance: 60,
      spinDuration: 1.0,
      fontFamily: cssState.wheelSlicesFont || "Montserrat",
      pointerProps: {
        style: {
          display: 'none',
        },
      },
    }),
    [
      mustSpin,
      prizeNumber,
      data,
      handleStopSpinning,
      cssState.borderColor,
      cssState.pointerColor,
      cssState.radiusLineColor,
      cssState.wheelSlicesFont,
      cssState.wheelSlicesFontSize,
    ]
  );

  const isWheelReady = data.length > 0;

  // Çark konteyneri için özel stil oluşturalım
  const wheelContainerStyle = useMemo(() => {
    // Wheel glow özelliklerini içeren stil
    if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
      try {
        const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
          cssState.wheelGlowOpacity
        })`;
        return {
          filter: `drop-shadow(0 0 50px ${wheelGlowRgba})`,
        };
      } catch (error) {
        console.error("Wheel stil oluşturma hatası:", error);
      }
    }

    // Varsayılan stil
    return {
      filter: "drop-shadow(0 0 50px rgba(9, 249, 132, 0.2))",
    };
  }, [cssState.wheelGlowColor, cssState.wheelGlowOpacity]);

  useEffect(() => {
    const root = document.documentElement;

    // CSS değişkenlerini güncelle
    root.style.setProperty("--button-gradient-1", cssState.buttonGradient1);
    root.style.setProperty("--button-gradient-2", cssState.buttonGradient2);
    root.style.setProperty("--button-text-color", cssState.buttonTextColor);
    root.style.setProperty("--button-border-color", cssState.buttonBorderColor);
    root.style.setProperty("--button-glow-color", cssState.buttonGlowColor);
    root.style.setProperty("--button-disabled-1", cssState.buttonDisabled1);
    root.style.setProperty("--button-disabled-2", cssState.buttonDisabled2);
    root.style.setProperty("--button-disabled-text", cssState.buttonDisabledText);
    root.style.setProperty("--button-disabled-border", cssState.buttonDisabledBorder);

    // Wheel glow özelliklerini güncelle
    try {
      // wheelGlowColor ve wheelGlowOpacity değerlerinin varlığını kontrol et
      if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
        const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
          cssState.wheelGlowOpacity
        })`;
        document.querySelectorAll(".wheel-container").forEach((container) => {
          container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
        });
      } else {
        // Varsayılan değerleri kullan
        const defaultGlow = `rgba(9, 249, 132, 0.2)`;
        document.querySelectorAll(".wheel-container").forEach((container) => {
          container.style.filter = `drop-shadow(0 0 50px ${defaultGlow})`;
        });
      }
    } catch (error) {
      console.error("Wheel glow güncellenirken hata:", error);
      // Hata durumunda varsayılan değer
      const defaultGlow = `rgba(9, 249, 132, 0.2)`;
      document.querySelectorAll(".wheel-container").forEach((container) => {
        container.style.filter = `drop-shadow(0 0 50px ${defaultGlow})`;
      });
    }

    // RGB değişkenlerini güncelle
    root.style.setProperty(
      "--button-border-color-rgb",
      getRGB(cssState.buttonBorderColor)
    );
    root.style.setProperty("--button-gradient-2-rgb", getRGB(cssState.buttonGradient2));

    // ---------- Logo margin (üst/alt/sol/sağ) + width ----------
    // width > 0 ise height: auto (aspect ratio korunarak büyür)
    // width = 0 ise default LONG boyutu (height 38, width auto)
    root.style.setProperty('--logo-margin-top', `${cssState.logoMarginTop || 0}px`);
    root.style.setProperty('--logo-margin-bottom', `${cssState.logoMarginBottom || 0}px`);
    root.style.setProperty('--logo-margin-left', `${cssState.logoMarginLeft || 0}px`);
    root.style.setProperty('--logo-margin-right', `${cssState.logoMarginRight || 0}px`);
    root.style.setProperty('--logo-padding-top', `${cssState.logoPaddingTop || 0}px`);
    root.style.setProperty('--logo-padding-bottom', `${cssState.logoPaddingBottom || 0}px`);
    root.style.setProperty('--logo-padding-left', `${cssState.logoPaddingLeft || 0}px`);
    root.style.setProperty('--logo-padding-right', `${cssState.logoPaddingRight || 0}px`);
    const logoW = cssState.logoWidth || 0;
    const logoH = cssState.logoHeight || 0;
    // Genişlik
    if (logoW > 0) root.style.setProperty('--logo-width', `${logoW}px`);
    else root.style.removeProperty('--logo-width');
    // Yükseklik: explicit > 0 ise sabit; değilse genişlik varsa auto (aspect korunur); ikisi de 0 ise CSS default (24/30px)
    if (logoH > 0) {
      root.style.setProperty('--logo-height', `${logoH}px`);
    } else if (logoW > 0) {
      root.style.setProperty('--logo-height', 'auto');
    } else {
      root.style.removeProperty('--logo-height');
    }

    // ---------- Tema primary (yedek değil, hep set'li) ----------
    // 170 temanın hepsinde olan borderColor / buttonBorderColor / pointerColor'dan türetilir
    const themePrimary = cssState.buttonBorderColor || cssState.borderColor || cssState.pointerColor || '#09f984';
    root.style.setProperty('--theme-primary', themePrimary);
    root.style.setProperty('--theme-primary-rgb', getRGB(themePrimary));

    // ---------- SilSurpur tarzı semantic token'lar (yeni editorial tasarım) ----------
    // Tema seçince silsüpürdeki gibi metin/muted de temadan türesin (foreground + ink-muted).
    // foreground = ana metin rengi; ink-muted = onun koyulaştırılmış (gri) hali. (Çark/segment
    // renkleri bunlardan ETKİLENMEZ — onlar kendi alanlarından gelir.)
    const fgColor = cssState.textColor1 || '#f4f4f5';
    root.style.setProperty('--foreground', fgColor);
    root.style.setProperty('--foreground-rgb', getRGB(fgColor));
    root.style.setProperty('--ink-muted', darkenHex(fgColor, 0.38));

    // ---------- Dekorasyon CSS değişkenleri ----------
    // Tema açıkça tanımlamamışsa borderColor/pointerColor/buttonGlowColor'dan türetilir
    // (Bu sayede 170 temanın hepsi çark dekorasyonunu da etkiler.)
    const ringAccent   = cssState.ringAccentColor   || cssState.borderColor || themePrimary;
    const standAccent  = cssState.standAccentColor  || cssState.borderColor || themePrimary;
    const cloverAccent = cssState.cloverColor       || cssState.pointerColor || themePrimary;
    const ambientAccent = cssState.ambientGlowColor || cssState.buttonGlowColor || themePrimary;

    // Ring (dış halka + ampuller + maça) — kendi RGB var'ı
    if (ringAccent) {
      root.style.setProperty('--ring-accent-rgb', getRGB(ringAccent));
      // Legacy: bazı yerlerde hala hue-rotate kullanılıyor olabilir
      const ringHue = hexToHue(ringAccent);
      root.style.setProperty('--ring-hue', `${Math.round(ringHue - 51)}deg`);
    }
    // Ambient glow (atmosferik halo) — kendi RGB var'ı
    if (ambientAccent) {
      root.style.setProperty('--ambient-glow-rgb', getRGB(ambientAccent));
    }
    // Stand (sehpa neck + base) — kendi RGB var'ı
    if (standAccent) {
      root.style.setProperty('--stand-accent-rgb', getRGB(standAccent));
    }
    // Clover (yonca işaretçi + sap) — kendi RGB var'ı + hue-rotate
    if (cloverAccent) {
      root.style.setProperty('--clover-accent-rgb', getRGB(cloverAccent));
      const cloverHue = hexToHue(cloverAccent);
      root.style.setProperty('--clover-hue', `${Math.round(cloverHue - 47)}deg`);
    }

    // ---------- Header granular tema alanları (Faz A) ----------
    // Her alan hem hex hem RGB formunda set'lenir → CSS hem solid hem alpha kullanabilsin
    // İkinci param fieldKey: admin alpha override için cssState[fieldKey + 'Alpha']
    const setColorVar = (cssName, color, fallback, alphaFieldKey) => {
      const val = color || fallback;
      if (val) {
        root.style.setProperty(`--${cssName}`, val);
        try { root.style.setProperty(`--${cssName}-rgb`, getRGB(val)); } catch {}
      }
      // Alpha override (DesignEditor sürgüsünden geliyor, 0-1 arası)
      if (alphaFieldKey) {
        const a = cssState[`${alphaFieldKey}Alpha`];
        if (a !== null && a !== undefined) {
          root.style.setProperty(`--${cssName}-alpha`, a);
        } else {
          root.style.removeProperty(`--${cssName}-alpha`);
        }
      }
    };

    // Welcome kart
    setColorVar('welcome-bg', cssState.welcomeBg, '#FFFFFF', 'welcomeBg');
    setColorVar('welcome-border', cssState.welcomeBorder, '#FFFFFF', 'welcomeBorder');
    setColorVar('welcome-border-hover', cssState.welcomeBorderHover, '#FFFFFF', 'welcomeBorderHover');
    // Avatar
    setColorVar('avatar-border', cssState.avatarBorder, themePrimary, 'avatarBorder');
    setColorVar('avatar-glow', cssState.avatarGlow, themePrimary, 'avatarGlow');
    setColorVar('avatar-bg-color', cssState.avatarBgColor, themePrimary, 'avatarBgColor');
    // Yazılar
    setColorVar('greeting-color', cssState.greetingColor, '#FFFFFF', 'greetingColor');
    setColorVar('username-color', cssState.usernameColor, themePrimary, 'usernameColor');
    setColorVar('subtitle-color', cssState.subtitleColor, '#FFFFFF', 'subtitleColor');
    // Chip
    setColorVar('chip-bg', cssState.chipBg, themePrimary, 'chipBg');
    setColorVar('chip-border', cssState.chipBorder, themePrimary, 'chipBorder');
    setColorVar('chip-text', cssState.chipText, themePrimary, 'chipText');
    // Aksiyon butonları (Hak Talep / Geçmiş) — yeni --ab-* değişken şeması
    setColorVar('ab-bg', cssState.actionBtnBg, '#FFFFFF', 'actionBtnBg');
    setColorVar('ab-border', cssState.actionBtnBorder, '#FFFFFF', 'actionBtnBorder');
    setColorVar('ab-text', cssState.actionBtnText, '#FFFFFF', 'actionBtnText');
    setColorVar('ab-hover-bg', cssState.actionBtnHoverBg, '#FFFFFF', 'actionBtnHoverBg');
    setColorVar('ab-hover-border', cssState.actionBtnHoverBorder, '#FFFFFF', 'actionBtnHoverBorder');
    setColorVar('ab-hover-text', cssState.actionBtnHoverText, '#FFFFFF', 'actionBtnHoverText');

    // ---------- Faz B: Modal granular tema alanları ----------
    // PrizeModal (Tebrikler) — modal-bg default tema arkaplan rengine bağlandı
    setColorVar('modal-overlay-bg',  cssState.modalOverlayBg,  '#000000', 'modalOverlayBg');
    setColorVar('modal-bg',          cssState.modalBg,         cssState.backgroundGradient1 || '#1A2234', 'modalBg');
    setColorVar('modal-border',      cssState.modalBorderColor, themePrimary, 'modalBorderColor');
    setColorVar('modal-title',       cssState.modalHeaderColor, '#FFFFFF', 'modalHeaderColor');
    setColorVar('modal-text',        cssState.modalTextColor,   '#FFFFFF', 'modalTextColor');
    setColorVar('modal-btn-grad-1',  cssState.modalButtonGradient1, themePrimary, 'modalButtonGradient1');
    setColorVar('modal-btn-grad-2',  cssState.modalButtonGradient2, themePrimary, 'modalButtonGradient2');
    setColorVar('modal-btn-text',    cssState.modalButtonText,  '#FFFFFF', 'modalButtonText');
    // Tik rozeti (Tebrikler check)
    setColorVar('check-bg',          cssState.checkBg,          themePrimary, 'checkBg');
    setColorVar('check-border',      cssState.checkBorder,      themePrimary, 'checkBorder');
    setColorVar('check-tick',        cssState.checkTick,        themePrimary, 'checkTick');
    // Ödül kutusu (Kazandığın Ödül)
    setColorVar('modal-prize-bg',      cssState.modalPrizeBg,      'rgba(255,255,255,0.025)', 'modalPrizeBg');
    setColorVar('modal-prize-border',  cssState.modalPrizeBorder,  'rgba(255,255,255,0.06)',  'modalPrizeBorder');
    setColorVar('modal-prize-label',   cssState.modalPrizeLabel,   'rgba(255,255,255,0.42)',  'modalPrizeLabel');
    setColorVar('modal-prize-value',   cssState.modalPrizeValue,   '#FFFFFF',                 'modalPrizeValue');

    // Sahne perdesi — temadan türetilen koyu vurgulu kırmızı
    // Kullanıcı yalnızca ana rengi değiştirse koyu ton ve kornis derinliği otomatik türer.
    const curtainDefault     = darkenHex(themePrimary, 0.55);
    const curtainMain        = cssState.curtainColor || curtainDefault;
    const corniceTopDefault  = '#d6a82a';
    const corniceTop         = cssState.curtainCornice || corniceTopDefault;
    setColorVar('curtain-color',         curtainMain,                                              curtainDefault,            'curtainColor');
    setColorVar('curtain-dark',          cssState.curtainDark    || darkenHex(curtainMain, 0.45),  darkenHex(curtainMain, 0.45), 'curtainDark');
    setColorVar('curtain-cornice',       corniceTop,                                               corniceTopDefault,         'curtainCornice');
    setColorVar('curtain-cornice-dark',  darkenHex(corniceTop, 0.30),                              '#9a7416',                 null);
    setColorVar('curtain-cornice-deep',  darkenHex(corniceTop, 0.55),                              '#5a4208',                 null);

    // HistoryModal
    setColorVar('history-overlay-bg', cssState.historyOverlayBg, '#000000', 'historyOverlayBg');
    setColorVar('history-bg',         cssState.historyModalBg,   '#1A2234', 'historyModalBg');
    setColorVar('history-border',     cssState.historyModalBorder, themePrimary, 'historyModalBorder');
    setColorVar('history-title',      cssState.historyHeaderColor, '#FFFFFF', 'historyHeaderColor');
    setColorVar('history-close',      cssState.historyCloseColor,  '#FFFFFF', 'historyCloseColor');
    setColorVar('history-date',       cssState.historyDateColor,   '#FFFFFF', 'historyDateColor');
    setColorVar('history-prize',      cssState.historyInfoValue,   '#FFFFFF', 'historyInfoValue');
    setColorVar('history-badge-bg',   cssState.historyBadgeBg,     themePrimary, 'historyBadgeBg');
    setColorVar('history-badge-text', cssState.historyBadgeText,   themePrimary, 'historyBadgeText');
    setColorVar('history-item-bg',    cssState.historyItemBg,      '#FFFFFF', 'historyItemBg');
    setColorVar('history-item-border', cssState.historyItemBorder, themePrimary, 'historyItemBorder');

    // NoSpinAlert
    setColorVar('nospin-overlay-bg', cssState.noSpinOverlayBg, '#000000', 'noSpinOverlayBg');
    setColorVar('nospin-bg',         cssState.noSpinBg,         '#0F0C12', 'noSpinBg');
    setColorVar('nospin-border',     cssState.noSpinBorder,     '#EF4444', 'noSpinBorder');
    setColorVar('nospin-title',      cssState.noSpinTitle,      '#FFFFFF', 'noSpinTitle');
    setColorVar('nospin-text',       cssState.noSpinText,       '#FFFFFF', 'noSpinText');
    setColorVar('nospin-x',          cssState.noSpinX,          '#EF4444', 'noSpinX');
    setColorVar('nospin-claim-bg',   cssState.noSpinClaimBg,    '#EF4444', 'noSpinClaimBg');
    setColorVar('nospin-claim-text', cssState.noSpinClaimText,  '#FFFFFF', 'noSpinClaimText');
    setColorVar('nospin-cancel-bg',  cssState.noSpinCancelBg,   '#FFFFFF', 'noSpinCancelBg');
    setColorVar('nospin-cancel-text', cssState.noSpinCancelText, '#FFFFFF', 'noSpinCancelText');

    // ResetConfirmationModal
    setColorVar('reset-overlay-bg', cssState.resetOverlayBg, '#000000', 'resetOverlayBg');
    setColorVar('reset-bg',         cssState.resetBg,        '#0F0C12', 'resetBg');
    setColorVar('reset-border',     cssState.resetBorder,    themePrimary, 'resetBorder');
    setColorVar('reset-title',      cssState.resetTitle,     '#FFFFFF', 'resetTitle');
    setColorVar('reset-text',       cssState.resetText,      '#FFFFFF', 'resetText');
    setColorVar('reset-cancel-bg',  cssState.resetCancelBg,  '#FFFFFF', 'resetCancelBg');
    setColorVar('reset-cancel-text', cssState.resetCancelText, '#FFFFFF', 'resetCancelText');
    setColorVar('reset-confirm-bg', cssState.resetConfirmBg, '#EF4444', 'resetConfirmBg');
    setColorVar('reset-confirm-text', cssState.resetConfirmText, '#FFFFFF', 'resetConfirmText');

    // Modal CSS değişkenleri
    root.style.setProperty('--modal-bg', cssState.modalBg || `linear-gradient(145deg, ${cssState.backgroundGradient1}, ${cssState.backgroundGradient2})`);
    root.style.setProperty('--modal-border-color', cssState.modalBorderColor || cssState.borderColor);
    root.style.setProperty('--modal-glow-rgb', getRGB(cssState.modalBorderColor || cssState.borderColor));
    root.style.setProperty('--modal-header-color', cssState.modalHeaderColor || cssState.textColor1);
    root.style.setProperty('--modal-text-color', cssState.modalTextColor || cssState.textColor2);
    root.style.setProperty('--modal-btn-gradient-1', cssState.modalButtonGradient1 || cssState.buttonGradient1);
    root.style.setProperty('--modal-btn-gradient-2', cssState.modalButtonGradient2 || cssState.buttonGradient2);
    root.style.setProperty('--modal-btn-text', cssState.modalButtonText || cssState.buttonTextColor);

    // Yazı tipleri CSS değişkenleri
    root.style.setProperty('--spin-button-font', quoteFont(cssState.spinButtonFont || "Poppins"));
    root.style.setProperty('--wheel-slices-font', quoteFont(cssState.wheelSlicesFont || "Montserrat"));
    root.style.setProperty('--wheel-slices-font-size', (cssState.wheelSlicesFontSize || 20) + 'px');
  }, [cssState]);

  // Yazı tiplerini dinamik olarak yükle (Google Fonts) - Türkçe Karakter Uyumlu (latin-ext)
  useEffect(() => {
    const loadGoogleFont = (fontFamily) => {
      if (!fontFamily) return;
      const webSafeFonts = ['Georgia', 'Impact', 'Arial', 'Arial Black', 'Helvetica', 'Times New Roman', 'Courier New', 'Trebuchet MS', 'Verdana'];
      if (webSafeFonts.includes(fontFamily)) return;

      const fontId = `gfont-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
      if (document.getElementById(fontId)) return;

      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      // weightsiz temiz çağrı -> Bad Request (400) hatalarını önler ve tüm yazı tiplerinin başarıyla yüklenmesini garanti eder
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}&display=swap`;
      document.head.appendChild(link);
    };

    loadGoogleFont(cssState.spinButtonFont);
    loadGoogleFont(cssState.wheelSlicesFont);

    // Fontlar yüklenince çarkı (Canvas) tetiklemek ve dinamik olarak yeniden çizdirmek için dinleyici
    if (typeof document.fonts !== "undefined") {
      document.fonts.ready.then(() => {
        setTimeout(() => {
          setFontLoadedTrigger((prev) => prev + 1);
        }, 150);
      });
    }
  }, [cssState.spinButtonFont, cssState.wheelSlicesFont]);

  // Wheel glow özelliklerini izle ve değiştiğinde uygula
  useEffect(() => {
    // Wheel glow değiştiğinde doğrudan uygula
    if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
      try {
        // CSS değişkenlerini document root'a ekle
        const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
          cssState.wheelGlowOpacity
        })`;
        console.log("Uygulanan wheelGlow (Direkt Stil):", wheelGlowRgba);

        // Özel CSS değişkeni oluştur
        document.documentElement.style.setProperty("--wheel-glow-color", wheelGlowRgba);

        // CSS kuralı oluştur
        const styleId = "dynamic-wheel-glow";
        let styleEl = document.getElementById(styleId);

        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.id = styleId;
          document.head.appendChild(styleEl);
        }

        // Tüm olası seçicileri kapsamak için genişletilmiş CSS
        styleEl.textContent = `
          .wheel-container {
            filter: drop-shadow(0 0 50px var(--wheel-glow-color)) !important;
          }
        `;
      } catch (error) {
        console.error("Wheel glow güncellenirken hata:", error);
      }
    }
  }, [cssState.wheelGlowColor, cssState.wheelGlowOpacity]);

  // Admin kontrolü için useEffect'i güncelle
  useEffect(() => {
    const checkAdminRoute = () => {
      if (isAdminRoute() && !isAdmin) {
        setShowAdminLogin(true);
      }
    };

    checkAdminRoute();
  }, [location.pathname, isAdmin]);

  // handleLogin fonksiyonunu güncelleyelim
  const handleLogin = () => {
    setIsAdmin(true);
    setShowAdminLogin(false);

    // Başarılı login alert'i göster
    setAlertConfig({
      show: true,
      message: "Giriş başarılı!",
      type: "success",
    });

    // Alert'i kapat ama sayfada kal
    setTimeout(() => {
      setAlertConfig({ show: false, message: "", type: "success" });
    }, 2000);
  };

  const handleAIChanges = (changes) => {
    // Her bir değişikliği uygula
    Object.entries(changes).forEach(([key, value]) => {
      handleColorChange(key, value);
    });

    // CSS değişkenlerini güncelle
    const root = document.documentElement;

    // Header arkaplan rengi
    if (changes.headerBg) {
      root.style.setProperty("--header-bg", changes.headerBg);
    }
    // Header kenar rengi
    if (changes.headerBorder) {
      root.style.setProperty("--header-border", changes.headerBorder);
    }
    // Header ikon rengi
    if (changes.headerIconColor) {
      root.style.setProperty("--header-icon-color", changes.headerIconColor);
    }
    // Diğer renk değişiklikleri...
  };

  // Popup durumlarını yönetmek için
  const handlePopupOpen = (popupName) => {
    // Tüm popup'ları kapat
    setShowWheelEditor(false);
    setShowDesignEditor(false);
    setShowAIChat(false);

    // Sadece istenen popup'ı aç
    switch (popupName) {
      case "wheel":
        setShowWheelEditor(true);
        break;
      case "design":
        setShowDesignEditor(true);
        break;
      case "chat":
        setShowAIChat(true);
        break;
      default:
        break;
    }
  };

  // Kullanıcı başlatma useEffect'ini güncelle
  useEffect(() => {
    const initializeUser = async () => {
      const userId = getUserIdFromUrl();

      // Sadece admin sayfasına erişmeye çalışıyorsa
      if (!userId && isAdminRoute()) {
        setInvalidAccess(true);
        setLoading(false);
        return;
      }

      // userId yoksa ve admin route'unda değilsek
      if (!userId && !isAdminRoute()) {
        setInvalidAccess(true);
        setLoading(false);
        return;
      }

      try {
        const validationResult = await validateUser(userId);

        if (validationResult) {
          const userData = await fetchUserData(userId);
          if (userData) {
            setUserName(userData.name);
            setSpinCount(userData.spin_count);
            setStatus(true);
          }
        } else {
          // Validasyon başarısız olduğunda
          setValidationError("Kullanıcı doğrulanamadı");
          return;
        }
      } catch (error) {
        console.error("Kullanıcı başlatma hatası:", error);
        setValidationError("Bir hata oluştu");
      } finally {
        if (!validationError) {
          setLoading(false);
        }
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    const loadCssData = async () => {
      try {
        const cssData = await fetchCarkCss();
        if (cssData) {
          // Hak Talep / Geçmiş butonları daima beyaz default — legacy kayıtlı renkleri override et
          cssData.actionBtnText = '#FFFFFF';
          cssData.actionBtnHoverText = '#FFFFFF';
          setCssState(cssData);
          console.log("API'den gelen CSS verileri:", cssData); // API verilerini kontrol et

          // CSS değişkenlerini güncelle
          Object.entries(cssData).forEach(([key, value]) => {
            // Base64 görsel ve boolean flag'ler CSS değişkeni olarak set edilmez
            if (key === "customBgImage" || key === "customBgEnabled" || key === "customLogoImage" || key === "customLogoEnabled" || key === "customAvatarImage" || key === "customAvatarEnabled") return;
            if (key === "longLogoVisible") {
              document.documentElement.style.setProperty(
                "--long-logo-display",
                value ? "flex" : "none"
              );
              return;
            }
            // Özel durumlar için kontroller
            if (key === "backgroundGradient1") {
              document.documentElement.style.setProperty("--bg-gradient-1", value);
            } else if (key === "backgroundGradient2") {
              document.documentElement.style.setProperty("--bg-gradient-2", value);
            } else if (key === "headerBg") {
              // Header bg için opacity ekle
              const rgbaValue = value.includes("rgba")
                ? value
                : value.replace(")", ", 0.7)").replace("rgb", "rgba");
              document.documentElement.style.setProperty("--header-bg", rgbaValue);
            } else if (key === "headerBorderColor" || key === "headerItemBorder") {
              // Border için opacity ekle
              const rgbaValue = value.includes("rgba")
                ? value
                : value.replace(")", ", 0.1)").replace("rgb", "rgba");
              document.documentElement.style.setProperty(
                `--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
                rgbaValue
              );
            } else if (key === "buttonGradient1") {
              document.documentElement.style.setProperty("--button-gradient-1", value);
            } else if (key === "buttonGradient2") {
              document.documentElement.style.setProperty("--button-gradient-2", value);
            } else {
              // Diğer tüm değişkenler için standart dönüşüm
              const cssVarName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
              let finalVal = value;
              if (key === "spinButtonFont" || key === "wheelSlicesFont") {
                finalVal = quoteFont(value);
              }
              document.documentElement.style.setProperty(`--${cssVarName}`, finalVal);
            }
          });

          // Wheel glow özelliklerini doğrudan uygula
          if (cssData.wheelGlowColor && typeof cssData.wheelGlowOpacity === "number") {
            try {
              const wheelGlowRgba = `rgba(${getRGB(cssData.wheelGlowColor)}, ${
                cssData.wheelGlowOpacity
              })`;
              document.querySelectorAll(".wheel-container").forEach((container) => {
                container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
              });
            } catch (error) {
              console.error("Wheel glow uygulanırken hata:", error);
            }
          }
        }
      } catch (error) {
        console.error("CSS yükleme hatası:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCssData();
  }, []);

  // Renk değişikliklerini sadece cssState'e uygula
  const handleColorChange = useCallback((key, value) => {
    setCssState((prev) => ({ ...prev, [key]: value }));

    // Long logo görünürlüğü: değer boolean — CSS variable olarak özel uygulanır
    if (key === "longLogoVisible") {
      document.documentElement.style.setProperty(
        "--long-logo-display",
        value ? "flex" : "none"
      );
      return;
    }

    // CSS değişkenlerini güncelle
    // Aksiyon butonları (Hak Talep / Geçmiş) — özel mapping (--ab-* şeması)
    const actionBtnMap = {
      actionBtnBg: 'ab-bg',
      actionBtnBorder: 'ab-border',
      actionBtnText: 'ab-text',
      actionBtnHoverBg: 'ab-hover-bg',
      actionBtnHoverBorder: 'ab-hover-border',
      actionBtnHoverText: 'ab-hover-text',
    };
    let finalValue = value;
    if (key === "spinButtonFont" || key === "wheelSlicesFont") {
      finalValue = quoteFont(value);
    } else if (key === "wheelSlicesFontSize") {
      finalValue = value + 'px';
    }
    const cssVarName = actionBtnMap[key] || key.replace(/([A-Z])/g, "-$1").toLowerCase();
    document.documentElement.style.setProperty(`--${cssVarName}`, finalValue);
    // RGB versiyonunu da otomatik set et — CSS rgba() kullanımı için (hex değer ise)
    if (typeof value === 'string' && value.startsWith('#') && value.length === 7) {
      try {
        const r = parseInt(value.slice(1, 3), 16);
        const g = parseInt(value.slice(3, 5), 16);
        const b = parseInt(value.slice(5, 7), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
          document.documentElement.style.setProperty(`--${cssVarName}-rgb`, `${r}, ${g}, ${b}`);
        }
      } catch {}
    }

    // Özel durumlar için ek kontroller
    if (key === "headerBg") {
      // RGBA değerini hesapla (70% opaklık)
      const rgbaValue = value.replace(")", ", 0.7)").replace("rgb", "rgba");
      document.documentElement.style.setProperty("--header-bg", rgbaValue);
    }

    if (key === "headerBorderColor" || key === "headerItemBorder") {
      // RGBA değerini hesapla (10% opaklık)
      const rgbaValue = value.replace(")", ", 0.1)").replace("rgb", "rgba");
      document.documentElement.style.setProperty(`--${cssVarName}`, rgbaValue);
    }

    if (key === "backgroundGradient1") {
      document.documentElement.style.setProperty("--bg-gradient-1", value);
    }

    if (key === "backgroundGradient2") {
      document.documentElement.style.setProperty("--bg-gradient-2", value);
    }

  }, []);

  const compressBgImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        // Hedef çıkış: tam 1920x1080 (16:9). Kaynak farklı oranda ise center-crop ile uydurulur.
        const TARGET_W = 1920;
        const TARGET_H = 1080;
        const TARGET_RATIO = TARGET_W / TARGET_H;
        const srcRatio = img.width / img.height;

        let sx, sy, sw, sh;
        if (srcRatio > TARGET_RATIO) {
          // Kaynak daha geniş → sağ/sol kırp
          sh = img.height;
          sw = sh * TARGET_RATIO;
          sx = (img.width - sw) / 2;
          sy = 0;
        } else {
          // Kaynak daha dar/uzun → üst/alt kırp
          sw = img.width;
          sh = sw / TARGET_RATIO;
          sx = 0;
          sy = (img.height - sh) / 2;
        }

        const canvas = document.createElement('canvas');
        canvas.width = TARGET_W;
        canvas.height = TARGET_H;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, TARGET_W, TARGET_H);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  const handleBgImageUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setAlertConfig({ show: true, message: "Lütfen geçerli bir görsel dosyası seçin", type: "error" });
      return;
    }
    try {
      const compressed = await compressBgImage(file);
      setCssState(prev => ({ ...prev, customBgImage: compressed, customBgEnabled: true }));
      setAlertConfig({ show: true, message: "Arkaplan görseli yüklendi — kaydetmek için 'CSS Kaydet' butonuna basın", type: "success" });
    } catch {
      setAlertConfig({ show: true, message: "Görsel işlenirken bir hata oluştu", type: "error" });
    }
  }, [compressBgImage]);

  const handleRemoveBgImage = useCallback(() => {
    setCssState(prev => ({ ...prev, customBgImage: null, customBgEnabled: false }));
  }, []);

  // ---------- Logo upload (PNG, max 600x200 — orijinal oran korunur) ----------
  const compressLogoImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX_W = 600;
        const MAX_H = 200;
        let { width, height } = img;
        // Aspect ratio korunarak MAX_W × MAX_H sınırına sığdır
        const ratio = Math.min(MAX_W / width, MAX_H / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        // PNG → transparency korunur
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  const handleLogoImageUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setAlertConfig({ show: true, message: "Lütfen geçerli bir görsel dosyası seçin", type: "error" });
      return;
    }
    try {
      const compressed = await compressLogoImage(file);
      setCssState(prev => ({ ...prev, customLogoImage: compressed, customLogoEnabled: true }));
      setAlertConfig({ show: true, message: "Logo yüklendi — kaydetmek için 'CSS Kaydet' butonuna basın", type: "success" });
    } catch {
      setAlertConfig({ show: true, message: "Logo işlenirken bir hata oluştu", type: "error" });
    }
  }, [compressLogoImage]);

  const handleRemoveLogoImage = useCallback(() => {
    setCssState(prev => ({ ...prev, customLogoImage: null, customLogoEnabled: false }));
  }, []);

  // ---------- Avatar upload (PNG, 200x200 kare center-crop) ----------
  const compressAvatarImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const TARGET = 200;
        // Kare center-crop
        const minSide = Math.min(img.width, img.height);
        const sx = (img.width - minSide) / 2;
        const sy = (img.height - minSide) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = TARGET;
        canvas.height = TARGET;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, TARGET, TARGET);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  const handleAvatarImageUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setAlertConfig({ show: true, message: "Lütfen geçerli bir görsel dosyası seçin", type: "error" });
      return;
    }
    try {
      const compressed = await compressAvatarImage(file);
      setCssState(prev => ({ ...prev, customAvatarImage: compressed, customAvatarEnabled: true }));
      setAlertConfig({ show: true, message: "Avatar yüklendi — kaydetmek için 'CSS Kaydet' butonuna basın", type: "success" });
    } catch {
      setAlertConfig({ show: true, message: "Avatar işlenirken bir hata oluştu", type: "error" });
    }
  }, [compressAvatarImage]);

  const handleRemoveAvatarImage = useCallback(() => {
    setCssState(prev => ({ ...prev, customAvatarImage: null, customAvatarEnabled: false }));
  }, []);

  const handleResetCss = () => {
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    try {
      setIsLoading(true);
      setShowResetModal(false);
      const result = await resetToDefaultCss();

      if (result.success) {
        setAlertConfig({
          show: true,
          message: "Varsayılan CSS ayarlarına başarıyla dönüldü",
          type: "success",
        });

        if (result.data) {
          setCssState(result.data);
          console.log("Sıfırlama sonrası CSS verileri:", result.data);

          // CSS değişkenlerini güncelle
          Object.entries(result.data).forEach(([key, value]) => {
            if (key === "customBgImage" || key === "customBgEnabled" || key === "customLogoImage" || key === "customLogoEnabled" || key === "customAvatarImage" || key === "customAvatarEnabled") return;
            if (key === "headerBg") {
              const rgbaValue = value.replace(")", ", 0.7)").replace("rgb", "rgba");
              document.documentElement.style.setProperty("--header-bg", rgbaValue);
            } else if (key === "headerBorderColor" || key === "headerItemBorder") {
              const rgbaValue = value.replace(")", ", 0.1)").replace("rgb", "rgba");
              document.documentElement.style.setProperty(
                `--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
                rgbaValue
              );
            } else if (key === "backgroundGradient1") {
              document.documentElement.style.setProperty("--bg-gradient-1", value);
            } else if (key === "backgroundGradient2") {
              document.documentElement.style.setProperty("--bg-gradient-2", value);
            } else if (key === "buttonGradient1") {
              document.documentElement.style.setProperty("--button-gradient-1", value);
            } else if (key === "buttonGradient2") {
              document.documentElement.style.setProperty("--button-gradient-2", value);
            } else {
              const cssVarName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
              document.documentElement.style.setProperty(`--${cssVarName}`, value);
            }
          });

          // Wheel glow özelliklerini doğrudan uygula
          if (
            result.data.wheelGlowColor &&
            typeof result.data.wheelGlowOpacity === "number"
          ) {
            try {
              const wheelGlowRgba = `rgba(${getRGB(result.data.wheelGlowColor)}, ${
                result.data.wheelGlowOpacity
              })`;
              document.querySelectorAll(".wheel-container").forEach((container) => {
                container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
              });
            } catch (error) {
              console.error("Wheel glow sıfırlama sırasında hata:", error);
            }
          }
        }
      } else {
        setAlertConfig({
          show: true,
          message: result.message || "Varsayılan CSS ayarlarına dönülemedi",
          type: "error",
        });
      }
    } catch (error) {
      console.error("CSS sıfırlama hatası:", error);
      setAlertConfig({
        show: true,
        message: "Varsayılan CSS ayarlarına dönülürken bir hata oluştu",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // history butonuna tıklandığında geçmişi yükle
  const handleHistoryClick = async () => {
    if (!mustSpin) {
      const userId = getUserIdFromUrl();
      if (userId) {
        const history = await fetchUserHistory(userId);
        if (history) {
          setHistoryData(history);
        }
        setShowHistory(true);
      }
    }
  };

  const handleSpinCountRequest = async () => {
    if (!mustSpin) {
      const userId = getUserIdFromUrl();
      if (userId) {
        setIsRequestingSpinCount(true);
        try {
          const spinCountResponse = await sendSpinCountRequest(userId);

          const isSuccess = !!spinCountResponse.status;
          setStatusModal({
            show: true,
            type: isSuccess ? 'success' : 'error',
            title: isSuccess ? 'Başarılı' : 'Talep Edilemedi',
            message: spinCountResponse.message || (isSuccess ? 'Hak talebiniz başarıyla alındı.' : 'Talep gerçekleştirilemedi.'),
          });

          // Eğer başarılı ise spin sayısını güncelle
          if (isSuccess && spinCountResponse.data) {
            setSpinCount(spinCountResponse.data.spin_count);
          }
        } catch (error) {
          setStatusModal({
            show: true,
            type: 'error',
            title: 'Bağlantı Hatası',
            message: 'Hak talep edilirken bir hata oluştu.',
          });
        } finally {
          setIsRequestingSpinCount(false);
        }
      }
    }
  };

  // URL'den userId'yi alan yardımcı fonksiyonu ekleyelim (zaten var ama tekrar ekleyelim)
  const getUserIdFromUrl = () => {
    const pathSegments = window.location.pathname.split("/");
    return pathSegments[1] || null;
  };

  // handleCloseLogin fonksiyonunu güncelleyelim
  const handleCloseLogin = () => {
    setShowAdminLogin(false);
    const userId = getUserIdFromUrl();

    if (userId) {
      navigate(`/${userId}`);
    } else {
      navigate("/");
    }
  };

  // Logo URL'sini oluşturan fonksiyonu güncelleyelim
  const getLogoUrl = () => {
    const baseUrl = window.location.origin; // http://localhost:3000
    return `${baseUrl}/image7.png`; // userId olmadan direkt image7.png'ye yönlendir
  };

  // useEffect içindeki logo yükleme kısmını güncelleyelim
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const logoUrl = getLogoUrl();
        const response = await fetch(logoUrl);

        if (!response.ok) {
          throw new Error("Logo yüklenemedi");
        }

        setLogoSrc(logoUrl);
      } catch (error) {
        console.error("Logo yükleme hatası:", error);
        // Logo yüklenemezse varsayılan bir logo kullanabilirsiniz
        setLogoSrc("/default-logo.png");
      }
    };

    loadLogo();
  }, []); // userId dependency'sini kaldırdık

  // Loading ve hata durumlarını kontrol et
  if (loading || validationError || isLoading) {
    return (
      <Loading
        error={!!validationError}
        message={validationError || "Yükleniyor..."}
        logoSrc={
          (cssState.customLogoEnabled && cssState.customLogoImage)
          || (typeof window !== 'undefined' && window.__INITIAL_LOGO__)
          || ""
        }
      />
    );
  }

  // Geçersiz erişim durumu
  if (invalidAccess) {
    return (
      <div className="invalid-access">
        <h1>Geçersiz Erişim</h1>
        <p>Bu sayfaya doğrudan erişim sağlanamaz.</p>
      </div>
    );
  }

  return (
    <div
      className="App"
      style={{
        backgroundImage: cssState.customBgEnabled && cssState.customBgImage
          ? `url('${cssState.customBgImage}')`
          : `url('/${activeBackground}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* CSS Butonları */}
      {isAdmin && (
        <>
          <AdminButtons onSaveCss={handleSaveCssChanges} onResetCss={handleResetCss} />

          <ResetConfirmationModal
            isOpen={showResetModal}
            onConfirm={handleConfirmReset}
            onCancel={() => setShowResetModal(false)}
          />
        </>
      )}

      <div className="ck-stage">
      <Header
        logoSrc={cssState.customLogoEnabled && cssState.customLogoImage ? cssState.customLogoImage : ""}
        avatarSrc={cssState.customAvatarEnabled && cssState.customAvatarImage ? cssState.customAvatarImage : ""}
        username={userName && userName !== "Kullanıcı" ? userName : "Misafir"}
        subtitle="Şansın bugün çarkın bir tık ötesinde."
        spinCount={spinCount}
        onClaim={handleSpinCountRequest}
        onHistory={handleHistoryClick}
        claimDisabled={mustSpin || isRequestingSpinCount}
        claimLoading={isRequestingSpinCount}
        historyDisabled={mustSpin}
        actionBtnBg={cssState.actionBtnBg}
        actionBtnBorder={cssState.actionBtnBorder}
        actionBtnText={cssState.actionBtnText}
        actionBtnHoverBg={cssState.actionBtnHoverBg}
        actionBtnHoverBorder={cssState.actionBtnHoverBorder}
        actionBtnHoverText={cssState.actionBtnHoverText}
      >

      <div className="casino-scene">
        <div className={`casino-wheel-wrap theme-${cssState.wheelType || 'cosmic'}`}>
          <div className="casino-ambient-glow" />
          
          {/* KOZMİK TEMA DIŞ KALKANI */}
          {cssState.wheelType === "cosmic" && (
            <>
              <div className="cosmic-shield-ring" />
              <div className="cosmic-wing left" />
              <div className="cosmic-wing right" />
            </>
          )}

          {/* ELMAS TEMA DIŞ ÇERÇEVESİ */}
          {cssState.wheelType === "diamond" && (
            <>
              <div className="luxury-diamond-ring" />
              <div className="luxury-diamond-top-ornament" />
              <div className="luxury-diamond-bottom-ornament" />
              {Array.from({ length: 24 }).map((_, i) => (
                <div
                  key={`diamond-cell-${i}`}
                  className="diamond-cell"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: `rotate(${i * (360 / 24)}deg) translateY(calc(var(--wheel-d) * -0.545)) rotate(-${i * (360 / 24)}deg) translate(-50%, -50%)`,
                    zIndex: 8,
                    pointerEvents: "none"
                  }}
                >
                  <div className="diamond-crystal-sparkle" />
                </div>
              ))}
            </>
          )}

          {/* KLASİK TEMA DIŞ ÇERÇEVESİ */}
          {(cssState.wheelType === "classic" || !cssState.wheelType) && (
            <div className="casino-outer-ring" />
          )}

          <div className={`wheel-container ${mustSpin ? 'is-spinning' : isWinning ? 'is-winning' : 'is-idle'}`} style={wheelContainerStyle}>
            {isWheelReady ? (
              <Wheel key={`wheel-${fontLoadedTrigger}`} {...wheelProps} />
            ) : (
              <div style={{ color: "#fff", textAlign: "center", padding: "24px 0" }}>
                Cark verileri yukleniyor...
              </div>
            )}

            {/* Sabit 3D Yansıma Overlay Katmanı */}
            {cssState.wheelOverlayShine !== false && (
              <div className={`wheel-shine-overlay ${cssState.wheelType || 'cosmic'}`} />
            )}

            {/* KOZMİK TEMA MERKEZ BUTONU */}
            {cssState.wheelType === "cosmic" && (
              <button
                className={`spin-button cosmic-spin-button ${mustSpin ? 'is-spinning' : ''}`}
                style={{
                  fontFamily: `'${cssState.spinButtonFont || "Poppins"}', sans-serif`,
                }}
                onClick={handleSpinClick}
                disabled={mustSpin || isSpinning || !isWheelReady}
              >
                <div className="cosmic-portal-bg" />
                <div className="cosmic-cog-teeth" />
                <span style={{ fontFamily: `'${cssState.spinButtonFont || "Poppins"}', sans-serif`, color: cssState.centerButtonTextColor || '#00f0ff' }}>
                  {mustSpin ? "BOL\nŞANSLAR" : "ÇEVİR"}
                </span>
              </button>
            )}

            {/* ELMAS TEMA MERKEZ BUTONU */}
            {cssState.wheelType === "diamond" && (
              <button
                className={`spin-button luxury-spin-button ${mustSpin ? 'is-spinning' : ''}`}
                style={{
                  fontFamily: `'${cssState.spinButtonFont || "Poppins"}', sans-serif`,
                }}
                onClick={handleSpinClick}
                disabled={mustSpin || isSpinning || !isWheelReady}
              >
                <div className="luxury-diamond-bg" style={{ backgroundColor: cssState.centerButtonCrystalColor || '#aadafa' }} />
                <span style={{ fontFamily: `'${cssState.spinButtonFont || "Poppins"}', sans-serif`, color: cssState.centerButtonTextColor || '#ffe893' }}>
                  {mustSpin ? "BOL\nŞANSLAR" : "ÇEVİR"}
                </span>
              </button>
            )}

            {/* KLASİK TEMA MERKEZ BUTONU */}
            {(cssState.wheelType === "classic" || !cssState.wheelType) && (
              <button
                className={`spin-button ${mustSpin ? 'is-spinning' : ''}`}
                style={{ fontFamily: `'${cssState.spinButtonFont || "Poppins"}', sans-serif` }}
                onClick={handleSpinClick}
                disabled={mustSpin || isSpinning || !isWheelReady}
              >
                <span style={{ fontFamily: `'${cssState.spinButtonFont || "Poppins"}', sans-serif` }}>
                  {mustSpin ? "BOL\nŞANSLAR" : "ÇEVİR"}
                </span>
              </button>
            )}
          </div>

          {/* KOZMİK TEMA İBRESİ */}
          {cssState.wheelType === "cosmic" && (
            <div className="cosmic-pointer">
              <svg viewBox="0 0 100 80" className="neon-chevron">
                <path d="M10,10 L50,55 L90,10" stroke={cssState.pointerColor || "#00f0ff"} strokeWidth="10" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M25,25 L50,70 L75,25" stroke={cssState.pointerColor2 || "#7000ff"} strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}

          {/* ELMAS TEMA İBRESİ */}
          {cssState.wheelType === "diamond" && (
            <div className="luxury-pointer">
              <div className="luxury-pointer-frame" />
              <div className="luxury-pointer-diamond" />
            </div>
          )}

          {/* KLASİK TEMA İBRESİ */}
          {(cssState.wheelType === "classic" || !cssState.wheelType) && (
            <div className="casino-pointer">
              <span className="casino-pointer-clover" style={{ color: cssState.cloverColor || '#09F984' }}>🍀</span>
              <div className="casino-pointer-stem" />
            </div>
          )}

          {/* KLASİK TEMA AMPUL VE MAÇA HALKASI */}
          {(cssState.wheelType === "classic" || !cssState.wheelType) && Array.from({ length: 28 }).map((_, i) => (
            <div
              key={`ring-${i}`}
              className={i % 2 === 0 ? "ring-bulb" : "ring-spade"}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `rotate(${i * (360 / 28)}deg) translateY(var(--casino-ring-radius, -358px)) rotate(-${i * (360 / 28)}deg) translate(-50%, -50%)`,
                zIndex: 8,
                pointerEvents: "none",
                // 28 element 1.4s'lik ardışık dalga halinde yanar (saat yönünde tek dalga)
                animationDelay: `${(i / 28) * 1.4}s`,
              }}
            >
              {i % 2 !== 0 && "♠"}
            </div>
          ))}
        </div>
        {(cssState.wheelType === "classic" || !cssState.wheelType) && (
          <div className="casino-stand">
            <div className="stand-neck" />
            <div className="stand-base" />
          </div>
        )}
      </div>
      </Header>
      </div>

      {/* Editör Butonları */}
      {isAdmin && (
        <>
          <div className="editor-buttons">
            <button
              className="editor-toggle wheel-editor-btn"
              onClick={() => handlePopupOpen("wheel")}
            >
              🎯 Çark Editörü
            </button>
            <button
              className="editor-toggle design-editor-btn"
              onClick={() => handlePopupOpen("design")}
            >
              🎨 Tasarım Editörü
            </button>
            <button
              className="editor-toggle hook-chat-btn"
              onClick={() => handlePopupOpen("chat")}
            >
              💬 Hook Chat <span className="beta-tag">Beta</span>
            </button>
          </div>

          {showThemeSelector && (
            <ThemeSelector
              onApplyTheme={(colors) => {
                // Eksik parıltı + dekorasyon alanlarını mevcut tema renginden türet
                const enriched = { ...colors };
                const accent =
                  enriched.buttonGlowColor ||
                  enriched.borderColor ||
                  enriched.pointerColor ||
                  enriched.buttonBorderColor;
                if (!enriched.wheelGlowColor)    enriched.wheelGlowColor = accent;
                if (typeof enriched.wheelGlowOpacity !== 'number') enriched.wheelGlowOpacity = 0.30;
                if (!enriched.ringAccentColor)   enriched.ringAccentColor   = accent;
                if (!enriched.standAccentColor)  enriched.standAccentColor  = accent;
                if (!enriched.cloverColor)       enriched.cloverColor       = accent;
                if (!enriched.ambientGlowColor)  enriched.ambientGlowColor  = accent;
                // Hak Talep / Geçmiş butonları daima beyaz — tema override etmesin
                enriched.actionBtnText = '#FFFFFF';
                enriched.actionBtnHoverText = '#FFFFFF';
                // Tüm modal renkleri tema'dan otomatik türev — tutarlı palet için
                const themeBg = enriched.backgroundGradient1 || enriched.modalBg;
                enriched.modalBg = themeBg;
                enriched.modalBorderColor = accent || enriched.modalBorderColor;
                // Tebrikler CTA butonu
                enriched.modalButtonGradient1 = accent || enriched.modalButtonGradient1;
                enriched.modalButtonGradient2 = darkenHex(accent, 0.35);
                enriched.modalButtonText = '#FFFFFF';
                // Geçmiş modalı
                enriched.historyModalBg = themeBg;
                enriched.historyModalBorder = accent;
                enriched.historyBadgeBg = accent;
                enriched.historyBadgeText = darkenHex(accent, 0.7);
                enriched.historyItemBorder = accent;
                Object.entries(enriched).forEach(([key, value]) => {
                  handleColorChange(key, value);
                });
              }}
            />
          )}
        </>
      )}

      {/* Çark Editörü */}
      {showWheelEditor && (
        <WheelEditor
          colors={cssState}
          segments={segments}
          onColorChange={handleColorChange}
          onClose={() => setShowWheelEditor(false)}
        />
      )}

      {/* Tasarım Editörü */}
      {showDesignEditor && (
        <DesignEditor
          colors={cssState}
          onColorChange={handleColorChange}
          onClose={() => setShowDesignEditor(false)}
          customBgImage={cssState.customBgImage}
          onBgImageUpload={handleBgImageUpload}
          onBgImageRemove={handleRemoveBgImage}
          customLogoImage={cssState.customLogoImage}
          onLogoImageUpload={handleLogoImageUpload}
          onLogoImageRemove={handleRemoveLogoImage}
          customAvatarImage={cssState.customAvatarImage}
          onAvatarImageUpload={handleAvatarImageUpload}
          onAvatarImageRemove={handleRemoveAvatarImage}
        />
      )}

      {showPrizeModal && (
        <div className="modal-overlay" onClick={() => setShowPrizeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Sahne perdeleri — kapalı başlar, animasyonla yana açılır */}
            <div className="modal-curtain modal-curtain--left" aria-hidden="true" />
            <div className="modal-curtain modal-curtain--right" aria-hidden="true" />

            <div className="modal-reveal">
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowPrizeModal(false)}
                aria-label="Kapat"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M1 1l12 12M13 1L1 13" />
                </svg>
              </button>

              <div className="modal-check" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              <h2 className="modal-title">Tebrikler!</h2>
              <p className="modal-subtitle">Yeni bir ödül kazandın.</p>

              <div className="modal-prize">
                <div className="modal-prize__label">Kazandığın Ödül</div>
                <div className="modal-prize__value">
                  {data[prizeNumber].option.split("\n").map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="modal-cta"
                onClick={() => setShowPrizeModal(false)}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="history-overlay" onClick={() => setShowHistory(false)}>
          <div className="history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="history-modal__header">
              <h2 className="history-modal__title">
                Geçmiş
                {historyData.length > 0 && (
                  <span className="history-modal__count">{historyData.length}</span>
                )}
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowHistory(false)}
                aria-label="Kapat"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M1 1l12 12M13 1L1 13" />
                </svg>
              </button>
            </div>

            <div className="history-modal__list">
              {historyData.length === 0 ? (
                <div className="history-modal__empty">
                  <svg className="history-modal__empty-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l4 2" />
                  </svg>
                  <p>Henüz geçmişin yok</p>
                </div>
              ) : (
                historyData.map((item) => {
                  const status = item.status || "success";
                  const statusLabel = status === "pending" ? "Beklemede" : status === "failed" ? "İptal" : null;
                  return (
                    <div key={item.id} className="history-row">
                      <span className={`history-row__dot history-row__dot--${status}`} aria-hidden="true" />
                      <div className="history-row__main">
                        <div className="history-row__prize">{item.win}</div>
                        <div className="history-row__date">{item.tarih}</div>
                      </div>
                      {statusLabel && (
                        <span className={`history-row__status history-row__status--${status}`}>
                          {statusLabel}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin Login Modal */}
      {showAdminLogin && <AdminLogin onLogin={handleLogin} onClose={handleCloseLogin} />}

      {showAIChat && (
        <AIChat onApplyChanges={handleAIChanges} onClose={() => setShowAIChat(false)} />
      )}

      {alertConfig.show && (
        <CustomAlert
          message={alertConfig.message}
          type={alertConfig.type}
          onClose={() => setAlertConfig({ show: false, message: "", type: "success" })}
        />
      )}

      {showNoSpinAlert && (
        <NoSpinAlert
          onClose={() => setShowNoSpinAlert(false)}
          onClaim={() => {
            setShowNoSpinAlert(false);
            handleSpinCountRequest();
          }}
          claimLoading={isRequestingSpinCount}
          colors={cssState}
        />
      )}

      {statusModal.show && (
        <StatusAlert
          type={statusModal.type}
          title={statusModal.title}
          message={statusModal.message}
          onClose={() => setStatusModal((s) => ({ ...s, show: false }))}
        />
      )}



      {/* ThemeSelector'ı isAdmin kontrolü altına al */}
      {isAdmin && (
        <ThemeSelector
          onApplyTheme={(colors) => {
            // Eksik dekorasyon + parıltı alanlarını mevcut tema renginden türet
            const enriched = { ...colors };
            const accent =
              enriched.buttonGlowColor ||
              enriched.borderColor ||
              enriched.pointerColor ||
              enriched.buttonBorderColor;
            if (!enriched.wheelGlowColor)    enriched.wheelGlowColor = accent;
            if (typeof enriched.wheelGlowOpacity !== 'number') enriched.wheelGlowOpacity = 0.30;
            if (!enriched.ringAccentColor)   enriched.ringAccentColor   = accent;
            if (!enriched.standAccentColor)  enriched.standAccentColor  = accent;
            if (!enriched.cloverColor)       enriched.cloverColor       = accent;
            if (!enriched.ambientGlowColor)  enriched.ambientGlowColor  = accent;
            // Hak Talep / Geçmiş butonları daima beyaz — tema override etmesin
            enriched.actionBtnText = '#FFFFFF';
            enriched.actionBtnHoverText = '#FFFFFF';
            // Tüm modal renkleri tema'dan otomatik türev — tutarlı palet için
            const themeBg = enriched.backgroundGradient1 || enriched.modalBg;
            enriched.modalBg = themeBg;
            enriched.modalBorderColor = accent || enriched.modalBorderColor;
            // Tebrikler CTA butonu
            enriched.modalButtonGradient1 = accent || enriched.modalButtonGradient1;
            enriched.modalButtonGradient2 = darkenHex(accent, 0.35);
            enriched.modalButtonText = '#FFFFFF';
            // Geçmiş modalı
            enriched.historyModalBg = themeBg;
            enriched.historyModalBorder = accent;
            enriched.historyBadgeBg = accent;
            enriched.historyBadgeText = darkenHex(accent, 0.7);
            enriched.historyItemBorder = accent;
            Object.entries(enriched).forEach(([key, value]) => {
              handleColorChange(key, value);
            });
          }}
        />
      )}

    </div>
  );
}

export default App;
